import React, { useMemo } from 'react';

import { Table, Tag } from 'antd';
import { RoiData } from 'routes/Dashboard/Diagnostic/Diagnostic.props';
//import { Container } from './PotentialRoi.style';
import {style} from 'd3';
import styled from 'utils/styled';

const Container = styled.div``;

export interface PotentialRoiProps {
  impact: RoiData;
  companyAnnualRevenue: number;
  getColorOfText: (percent: number) => string;
  desiredNetIncomePct: number;
  currentNetIncomePct: number;
  /**
   * Annual Revenue x Net Income Margin
   */
  currentNetIncome: number;
  innerRef?: React.RefObject<HTMLDivElement>;
}

const PotentialRoi = (props: PotentialRoiProps): JSX.Element => {
  // const theme = useTheme();
  // MARK:- State

  // MARK:- Transform
  const data = Object.keys(props.impact).map((key) => ({
    name: key,
    ...props.impact[key],
  }));

  // MARK:- Helper Functions
  function toCurrency(val: number): string {
    return val.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  }

  // MARK:- Memo
  const potentialImpact = useMemo(() => {
    let allAverages = 0;
    let allHigh = 0;
    let allLow = 0;
    let allAveragesPct = 0;

    let ndx = 0;

    data.forEach((d) => {
      allAveragesPct += d.avg;
      allAverages += (d.avg * props.companyAnnualRevenue) / 100;
      allHigh += (d.high * props.companyAnnualRevenue) / 100;
      allLow += (d.low * props.companyAnnualRevenue) / 100;
      ndx += 1;
    });

    const averagePct = allAveragesPct / ndx;

    return {
      high: allHigh,
      low: allLow,
      avg: allAverages,
      averagePct: averagePct,
    };
  }, [data, props.companyAnnualRevenue]);

  const potentialRevenueImprovement = useMemo(() => {
    return [
      {
        name: 'Current Revenue',
        avg: toCurrency(props.companyAnnualRevenue),
        high: toCurrency(props.companyAnnualRevenue),
        low: toCurrency(props.companyAnnualRevenue),
      },
      {
        name: 'Potential New Revenue',
        avg: toCurrency(potentialImpact.avg + props.companyAnnualRevenue),
        high: toCurrency(potentialImpact.high + props.companyAnnualRevenue),
        low: toCurrency(potentialImpact.low + props.companyAnnualRevenue),
      },
      {
        name: 'Growth',
        avg:
          ((potentialImpact.avg / props.companyAnnualRevenue) * 100).toFixed(
            2
          ) + '%',
        high:
          ((potentialImpact.high / props.companyAnnualRevenue) * 100).toFixed(
            2
          ) + '%',
        low:
          ((potentialImpact.low / props.companyAnnualRevenue) * 100).toFixed(
            2
          ) + '%',
      },
    ];
  }, [potentialImpact, props.companyAnnualRevenue]);

  const currentNetIncomeTable = useMemo(() => {
    const currentNetIncome = toCurrency(props.currentNetIncome);
    const currentNetIncomeDecimal = props.currentNetIncomePct / 100;

    const avgNetIncome =
      (potentialImpact.avg + props.companyAnnualRevenue) *
      currentNetIncomeDecimal;
    const highNetIncome =
      (potentialImpact.high + props.companyAnnualRevenue) *
      currentNetIncomeDecimal;
    const lowNetIncome =
      (potentialImpact.low + props.companyAnnualRevenue) *
      currentNetIncomeDecimal;

    const avgDifference = avgNetIncome - props.currentNetIncome;
    const highDifference = highNetIncome - props.currentNetIncome;
    const lowDifference = lowNetIncome - props.currentNetIncome;

    const avgGrowth = (avgDifference / props.currentNetIncome) * 100;
    const highGrowth = (highDifference / props.currentNetIncome) * 100;
    const lowGrowth = (lowDifference / props.currentNetIncome) * 100;

    return [
      {
        name: 'Current Net Income',
        avg: currentNetIncome,
        high: currentNetIncome,
        low: currentNetIncome,
      },
      {
        name: `Potential New Net Income ${props.currentNetIncomePct}%`,
        avg: toCurrency(avgNetIncome),
        high: toCurrency(highNetIncome),
        low: toCurrency(lowNetIncome),
      },
      {
        name: 'Difference',
        avg: toCurrency(avgDifference),
        high: toCurrency(highDifference),
        low: toCurrency(lowDifference),
      },
      {
        name: 'Growth',
        avg: avgGrowth.toFixed(2) + '%',
        high: highGrowth.toFixed(2) + '%',
        low: lowGrowth.toFixed(2) + '%',
      },
      {
        name: 'Five Year Profit Impact',
        avg: toCurrency(avgDifference * 5),
        high: toCurrency(highDifference * 5),
        low: toCurrency(lowDifference * 5),
      },
    ];
  }, [
    props.currentNetIncome,
    potentialImpact,
    props.currentNetIncomePct,
    props.companyAnnualRevenue,
  ]);

  const desiredNetIncomeTable = useMemo(() => {
    const desiredNetIncomeDecimal = props.desiredNetIncomePct / 100;
    const desiredNetIncome =
      props.currentNetIncome + props.currentNetIncome * desiredNetIncomeDecimal;

    const avgNetIncome =
      (potentialImpact.avg + props.companyAnnualRevenue) *
      desiredNetIncomeDecimal;
    const highNetIncome =
      (potentialImpact.high + props.companyAnnualRevenue) *
      desiredNetIncomeDecimal;
    const lowNetIncome =
      (potentialImpact.low + props.companyAnnualRevenue) *
      desiredNetIncomeDecimal;

    const avgDifference = avgNetIncome - props.currentNetIncome;
    const highDifference = highNetIncome - props.currentNetIncome;
    const lowDifference = lowNetIncome - props.currentNetIncome;

    const avgGrowth = (avgDifference / props.currentNetIncome) * 100;
    const highGrowth = (highDifference / props.currentNetIncome) * 100;
    const lowGrowth = (lowDifference / props.currentNetIncome) * 100;

    return [
      {
        name: 'Current Net Income',
        avg: toCurrency(props.currentNetIncome),
        high: toCurrency(props.currentNetIncome),
        low: toCurrency(props.currentNetIncome),
      },
      {
        name: `Potential New Net Income ${props.desiredNetIncomePct}%`,
        avg: toCurrency(avgNetIncome),
        high: toCurrency(highNetIncome),
        low: toCurrency(lowNetIncome),
      },
      {
        name: 'Difference',
        avg: toCurrency(avgDifference),
        high: toCurrency(highDifference),
        low: toCurrency(lowDifference),
      },
      {
        name: 'Growth',
        avg: avgGrowth.toFixed(2) + '%',
        high: highGrowth.toFixed(2) + '%',
        low: lowGrowth.toFixed(2) + '%',
      },
      {
        name: 'Five Year Profit Impact',
        avg: toCurrency(avgDifference * 5),
        high: toCurrency(highDifference * 5),
        low: toCurrency(lowDifference * 5),
      },
    ];
  }, [
    potentialImpact,
    props.desiredNetIncomePct,
    props.companyAnnualRevenue,
    props.currentNetIncome,
  ]);

  return (
    <Container ref={props.innerRef}>
      <p>
        <strong>Company Annual Revenue:</strong>{' '}
        {toCurrency(props.companyAnnualRevenue)}
      </p>

      <p>
        <strong>Current Net Income:</strong>{' '}
        {toCurrency(props.currentNetIncome)}
      </p>
      <p>
        <strong>Current Net Income Margin:</strong> {props.currentNetIncomePct}%
      </p>
      <p>
        <strong>Benchmark Net Income Margin:</strong>{' '}
        {props.desiredNetIncomePct}%
      </p>
      <Table
        dataSource={data}
        pagination={false}
        style={{ marginBottom: '40px' }}
        onHeaderRow={(columns, index) => ({ className: 'heatmap-table-header' })}
        summary={() => {
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>Potential Impact</Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                <div style={{ textAlign: 'center' }}>
                  <Tag>{potentialImpact.averagePct.toFixed(2)}%</Tag>
                </div>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                <div style={{ textAlign: 'center' }}>
                  {toCurrency(potentialImpact.low)}
                </div>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                <div style={{ textAlign: 'center' }}>
                  {toCurrency(potentialImpact.avg)}
                </div>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                <div style={{ textAlign: 'center' }}>
                  {toCurrency(potentialImpact.high)}
                </div>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          );
        }}
      >
        <Table.Column
          title="Category"
          dataIndex="name"
          width={200}
        />
        <Table.Column
          title="Score"
          width={400}
          render={({ avg, high, low }) => (
            <div style={{ textAlign: 'center' }}>
              <Tag>Low - {low.toFixed(2)}%</Tag>
              <Tag>Average - {avg.toFixed(2)}%</Tag>
              <Tag>High - {high.toFixed(2)}%</Tag>
            </div>
          )}
        />
        <Table.Column
          title="Impact Low"
          dataIndex="low"
          align='center'
          width={250}
          render={(low: number) => (<>{toCurrency((low * props.companyAnnualRevenue) / 100)}</>)}
        />
        <Table.Column
          title="Impact AVG"
          dataIndex="avg"
          align='center'
          width={250}
          onCell={(r, index) => ({ style: { fontWeight: 'bold' }})}
          render={(avg: number) => (<>{toCurrency((avg * props.companyAnnualRevenue) / 100)}</>)}
        />
        <Table.Column
          title="Impact High"
          dataIndex="high"
          align='center'
          width={250}
          render={(high: number) => (<>{toCurrency((high * props.companyAnnualRevenue) / 100)}</>)}
        />
      </Table>


      <h4 className="heatmap-title">Potential Revenue Growth</h4>
      <Table
        pagination={false}
        dataSource={potentialRevenueImprovement}
        style={{ marginBottom: '40px' }}
        onHeaderRow={(columns, index) => ({ className: 'heatmap-table-header' })}
        columns={[
          { title: 'Financial Opportunity', dataIndex: 'name', width: 600 },
          { title: 'Low', dataIndex: 'low', align: 'center', width: 250 },
          { 
            title: 'Average', 
            dataIndex: 'avg',
            align: 'center',
            onCell: (r, index) => ({ style: { fontWeight: 'bold' }}),
            width: 250,
          },
          { title: 'High', dataIndex: 'high', align: 'center', width: 250 },
        ]}
      />

      <h4 className="heatmap-title">Potential Net Income Income Growth (Current Margin)</h4>
      <Table
        pagination={false}
        dataSource={currentNetIncomeTable}
        style={{ marginBottom: '40px' }}
        onHeaderRow={(columns, index) => ({ className: 'heatmap-table-header' })}
        columns={[
          { title: 'Financial Opportunity', dataIndex: 'name', width: 600 },
          { title: 'Low', dataIndex: 'low', align: 'center', width: 250 },
          { 
            title: 'Average', 
            dataIndex: 'avg',
            align: 'center',
            onCell: (r, index) => ({ style: { fontWeight: 'bold' }}),
            width: 250,
          },
          { title: 'High', dataIndex: 'high', align: 'center', width: 250 },
        ]}
      >
      </Table>

      <h4 className="heatmap-title">Potential Net Income Growth (Benchmark Margin)</h4>
      <Table
        pagination={false}
        dataSource={desiredNetIncomeTable}
        style={{ marginBottom: '40px' }}
        onHeaderRow={(columns, index) => ({ className: 'heatmap-table-header' })}
        columns={[
          { title: 'Financial Oppurtunity', dataIndex: 'name', width: 600 },
          { title: 'Low', dataIndex: 'low', align: 'center', width: 250 },
          { 
            title: 'Average',
            dataIndex: 'avg',
            align: 'center',
            onCell: (r, index) => ({ style: { fontWeight: 'bold' }}),
            width: 250,
          },
          { title: 'High', dataIndex: 'high', align: 'center', width: 250 },
        ]}
      />
    </Container>
  );
};

export default PotentialRoi;
